import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import paths from "routes/paths";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Spinner,
  Breadcrumb,
  Button,
  Modal,
  Accordion,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import MultiSelect from "components/common/multiSelect";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import moment from "moment";
import Select from "react-select";
import SubtleBadge from "components/common/subtleBadge";
import {
  faStream,
  faCheck,
  faBan,
  faUser,
  faArrowRight,
  faCalendarAlt,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { 
  fetchRoleAndClaims, 
  handleLoadingAndAccess, 
  fetchLeaveConflictMap, 
  PlanningConflictModal,
  checkEmployeeAvailability,
  fetchRecommendedEmployees,
  partitionRecommendedEmployees,
  sortEmployeesAscending,
  clearEmployeeAvailabilityCache
} from "../../assets/js/common";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DatePicker from "react-datepicker";
import TimeInput from "../proposals/timeInput";
import { useForm, Controller } from "react-hook-form";
import { setHours, setMinutes } from "date-fns";
import { getOccurrenceCount } from "../../utils/recurrenceHelpers";

const safeParseSelectedDays = (raw) => {
  try {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "object" && parsed !== null) {
        return parsed.selectedDays || [];
      }
    }
    return [];
  } catch {
    return [];
  }
};

const safeParseAdvancedRecurrence = (raw) => {
  try {
    if (typeof raw === "string") {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {}
  return null;
};

const getWeekdayOccurrenceOfMonth = (year, month, occurrence, weekday) => {
  const dayIndex = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
    .indexOf(weekday.toLowerCase());
  if (dayIndex === -1) return null;

  let date = moment().year(year).month(month).date(1).startOf("day");
  const matchedDates = [];

  while (date.month() === month) {
    if (date.day() === dayIndex) {
      matchedDates.push(date.clone());
    }
    date.add(1, "day");
  }

  if (occurrence === "1st") return matchedDates[0];
  if (occurrence === "2nd") return matchedDates[1];
  if (occurrence === "3rd") return matchedDates[2];
  if (occurrence === "4th") return matchedDates[3];
  if (occurrence === "last") return matchedDates[matchedDates.length - 1];

  return null;
};

const getDailyWeeklyOccurrenceDates = (task, taskSelectedDays, orderStartDate, rangeEnd) => {
  const days = taskSelectedDays[task.ProposalTaskId] || task.recurrence?.selectedDays || [];
  if (!days.length) return [];
  const dates = [];
  let cursor = moment(orderStartDate).startOf("isoWeek");

  while (cursor.isSameOrBefore(rangeEnd, "day")) {
    days.forEach((day) => {
      const dayIndex = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
        .indexOf(day.toLowerCase());
      if (dayIndex === -1) return;
      const d = moment(cursor).add(dayIndex, "days");
      if (d.isSameOrAfter(orderStartDate, "day") && d.isSameOrBefore(rangeEnd, "day")) {
        dates.push(d);
      }
    });
    cursor.add(1, "week");
  }
  return dates;
};

const getMonthlyOccurrenceDates = (task, orderStartDate, rangeEnd) => {
  const adv = task.recurrence?.advancedRecurrence || {};
  const repeatEvery = task.recurrence?.repeatEvery || task.RepeatEvery || 1;
  const dates = [];
  let cursor = moment(orderStartDate).startOf("month");
  let monthCounter = 0;

  while (cursor.isSameOrBefore(rangeEnd, "month")) {
    if (monthCounter % repeatEvery === 0) {
      if (adv.monthlyType === "specific_day" || !adv.monthlyType) {
        const mDay = adv.monthlyDay || task.recurrence?.monthlyDay || task.MonthlyDay || 1;
        const targetDate = moment(cursor).date(mDay);
        if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
          dates.push(targetDate);
        }
      } else {
        let occurrences = [];
        if (Array.isArray(adv.monthlyOccurrences)) {
          occurrences = adv.monthlyOccurrences;
        } else if (adv.monthlyWeekOccurrence && adv.monthlyWeekday) {
          const parts = adv.monthlyWeekOccurrence.split("_");
          parts.forEach((p) => {
            occurrences.push({ occurrence: p, weekday: adv.monthlyWeekday });
          });
        }
        occurrences.forEach((occObj) => {
          const matchedDate = getWeekdayOccurrenceOfMonth(cursor.year(), cursor.month(), occObj.occurrence, occObj.weekday);
          if (matchedDate && matchedDate.isSameOrAfter(orderStartDate, "day") && matchedDate.isSameOrBefore(rangeEnd, "day")) {
            dates.push(matchedDate);
          }
        });
      }
    }
    cursor.add(1, "month");
    monthCounter++;
  }
  return dates;
};

const getYearlyOccurrenceDates = (task, orderStartDate, rangeEnd) => {
  const adv = task.recurrence?.advancedRecurrence || {};
  const dates = [];
  let cursor = moment(orderStartDate).startOf("year");

  while (cursor.isSameOrBefore(rangeEnd, "year")) {
    if (Array.isArray(adv.dates) && adv.dates.length > 0) {
      adv.dates.forEach((entry) => {
        const monthCursor = moment(cursor).month(Number(entry.month) || 0);
        const clampedDay = entry.day === "last"
          ? monthCursor.daysInMonth()
          : Math.min(Number(entry.day) || 1, monthCursor.daysInMonth());
        const targetDate = moment(monthCursor).date(clampedDay);
        if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
          dates.push(targetDate);
        }
      });
    } else if (adv.yearlyType === "weekday_occurrence" && Array.isArray(adv.yearlyOccurrences)) {
      adv.yearlyOccurrences.forEach((occObj) => {
        const monthCursor = moment(cursor).month(occObj.month);
        const matchedDate = getWeekdayOccurrenceOfMonth(monthCursor.year(), monthCursor.month(), occObj.occurrence, occObj.weekday);
        if (matchedDate && matchedDate.isSameOrAfter(orderStartDate, "day") && matchedDate.isSameOrBefore(rangeEnd, "day")) {
          dates.push(matchedDate);
        }
      });
    } else {
      let targetMonths = adv.yearlyMonth;
      if (!targetMonths || (Array.isArray(targetMonths) && targetMonths.length === 0)) {
        targetMonths = [task.recurrence?.yearlyMonth || task.YearlyMonth || 0];
      }
      if (!Array.isArray(targetMonths)) targetMonths = [Number(targetMonths)];

      targetMonths.forEach((mIndex) => {
        const monthCursor = moment(cursor).month(mIndex);
        const yDayRaw = adv.yearlyDay ?? task.recurrence?.yearlyDay ?? task.YearlyDay ?? 1;
        const clampedDay = yDayRaw === "last" ? monthCursor.daysInMonth() : Math.min(Number(yDayRaw) || 1, monthCursor.daysInMonth());
        const targetDate = moment(monthCursor).date(clampedDay);
        if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
          dates.push(targetDate);
        }
      });
    }
    cursor.add(1, "year");
  }
  return dates;
};

const buildInFormConflictSlots = ({ taskWithSubtasks, activeTaskId, taskAssignments, getValues, relationName }) => {
  const inFormSlotsByDay = {};

  taskWithSubtasks
    .filter((task) => task.ProposalTaskId !== activeTaskId)
    .forEach((task) => {
      const assignedIds = taskAssignments[task.ProposalTaskId] || [];
      const otherStartRaw = getValues(`editStartTime-${task.ProposalTaskId}`);
      if (!otherStartRaw) return;

      const isOtherOneTime = task.recurrence?.frequency === "OneTime";
      const otherStartUtc = moment.utc(otherStartRaw);
      const otherStartTimeOfDay = moment.utc(`1970-01-01T${otherStartUtc.format("HH:mm:ss")}`).local();
      const otherEndTimeOfDay = otherStartTimeOfDay.clone().add(task.DurationMinutes || 60, "minutes");

      assignedIds.forEach((employeeId) => {
        const slot = {
          id: task.ProposalTaskId,
          employeeId,
          start: otherStartTimeOfDay.toDate(),
          end: otherEndTimeOfDay.toDate(),
          label: task.taskName,
          clientName: relationName || null,
        };

        if (isOtherOneTime) {
          (inFormSlotsByDay["__onetime__"] ||= []).push(slot);
        } else {
          (task.recurrence?.selectedDays || []).forEach((day) => {
            const dayKey = day.toLowerCase();
            (inFormSlotsByDay[dayKey] ||= []).push({ ...slot, dayKey });
          });
        }
      });
    });

  return Object.entries(inFormSlotsByDay).flatMap(([dayKey, slots]) =>
    slots.map((slot) => ({
      ...slot,
      dayKey,
      date: null,
    }))
  );
};

const validateTaskFrequencies = (taskWithSubtasks, taskSelectedDays) => {
  for (const task of taskWithSubtasks) {
    const freq = task.recurrence?.frequency;
    if (
      (freq === "Weekly" || freq === "Daily") &&
      (!taskSelectedDays[task.ProposalTaskId] ||
        taskSelectedDays[task.ProposalTaskId].length === 0)
    ) {
      return false;
    }
  }
  return true;
};

const validateOneTimeDates = (taskWithSubtasks, taskOneTimeDates, orderStart, today) => {
  for (const task of taskWithSubtasks) {
    if (task.recurrence?.frequency !== "OneTime") continue;

    const effectiveDateRaw = taskOneTimeDates[task.ProposalTaskId] || task.recurrence?.advancedRecurrence?.date;
    const effectiveDate = moment(effectiveDateRaw).startOf("day");
    if (!effectiveDate.isValid()) continue;

    if (effectiveDate.isBefore(today)) {
      return { valid: false, errorKey: "taskDateCannotBePast" };
    }
    if (effectiveDate.isBefore(orderStart)) {
      return { valid: false, errorKey: "taskDateBeforeStartDate" };
    }
  }
  return { valid: true };
};

const calculateEffectiveOrderEnd = (orderStart, endDateRaw, taskWithSubtasks, taskOneTimeDates) => {
  let orderEnd = endDateRaw && moment(endDateRaw).isValid()
    ? moment(endDateRaw).startOf("day")
    : moment(orderStart).add(6, "months").startOf("day");

  const oneTimeTaskDatesForEnd = taskWithSubtasks
    .filter((t) => t?.recurrence?.frequency === "OneTime")
    .map((t) => moment(taskOneTimeDates[t.ProposalTaskId] || t.recurrence?.advancedRecurrence?.date))
    .filter((d) => d.isValid());

  const latestOneTimeTaskDate = oneTimeTaskDatesForEnd.length > 0
    ? moment.max(oneTimeTaskDatesForEnd)
    : null;

  if (latestOneTimeTaskDate && orderEnd.isBefore(latestOneTimeTaskDate)) {
    orderEnd = latestOneTimeTaskDate.startOf("day");
  }

  return orderEnd;
};

const createSingleOrderDetail = ({ task, cleanerId, taskDate, orderId, getValues }) => {
  const taskStartRaw = getValues(`editStartTime-${task.ProposalTaskId}`);
  const orderEndRaw = getValues("editEndTime");
  const taskDateStr = taskDate.format("YYYY-MM-DD");

  const taskStartHHmm = taskStartRaw ? moment.utc(taskStartRaw).format("HH:mm") : null;
  const orderEndHHmm = orderEndRaw ? moment.utc(orderEndRaw).format("HH:mm") : null;

  const taskStartMoment = taskStartHHmm
    ? moment.utc(`${taskDateStr}T${taskStartHHmm}`, "YYYY-MM-DDTHH:mm")
    : null;

  const taskEndMoment = (() => {
    if (taskStartMoment && orderEndHHmm) {
      const candidate = moment.utc(`${taskDateStr}T${orderEndHHmm}`, "YYYY-MM-DDTHH:mm");
      return candidate.isAfter(taskStartMoment)
        ? candidate
        : taskStartMoment.clone().add(1, "hour");
    }
    return taskStartMoment
      ? taskStartMoment.clone().add(1, "hour")
      : null;
  })();

  const durationMinutes = (taskStartMoment && taskEndMoment)
    ? taskEndMoment.diff(taskStartMoment, "minutes")
    : task.DurationMinutes || 60;
  const BASE_DATE = "1970-01-01";
  const payloadStartTime = taskStartHHmm
    ? moment.utc(`${BASE_DATE}T${taskStartHHmm}`, "YYYY-MM-DDTHH:mm").toISOString()
    : null;
  const payloadEndTime = taskEndMoment
    ? moment.utc(`${BASE_DATE}T${taskEndMoment.format("HH:mm")}`, "YYYY-MM-DDTHH:mm").toISOString()
    : null;

  return {
    OrderDetailId: uuidv4(),
    OrderId: orderId,
    TaskId: task.TaskId,
    ProposalTaskId: task.ProposalTaskId,
    WeekStartDate: taskDate.format("YYYY-MM-DD"),
    DayOfWeek: taskDate.format("dddd").toLowerCase(),
    StartTime: payloadStartTime,
    EndTime: payloadEndTime,
    DurationMinutes: durationMinutes,
    CleanerId: cleanerId,
    Status: "0",
    Location: null,
    Notes: "",
  };
};

const checkDatesOrTimesChanged = (originalDates, orderStart, orderEnd, startTimeRaw, endTimeRaw) => {
  const startDateChanged = !moment(originalDates?.startDate).isSame(orderStart, "day");
  const endDateChanged = !moment(originalDates?.endDate).isSame(orderEnd, "day");
  const startTimeChanged = startTimeRaw && originalDates?.startTime
    ? !moment(originalDates.startTime).isSame(moment(startTimeRaw), "minute")
    : false;
  const endTimeChanged = endTimeRaw && originalDates?.endTime
    ? !moment(originalDates.endTime).isSame(moment(endTimeRaw), "minute")
    : false;
  return startDateChanged || endDateChanged || startTimeChanged || endTimeChanged;
};

const dispatchRescheduleNotifications = async ({
  taskAssignments,
  newlyAddedCleanersByTask,
  allPreviousCleanersByTask,
  resolveCleanerById,
  customerId,
  updatedDetailsPayload,
  buildOrderInfo,
  culture,
  userId,
  orderId,
  updatedOrderPayload,
  relationData,
  t,
  dispatchBatchNotification,
}) => {
  const allAssignedCleanerIds = new Set(Object.values(taskAssignments).flat());
  const rescheduleEntries = [];

  await Promise.all([...allAssignedCleanerIds].map(async (cleanerId) => {
    const isNewlyAdded = Object.entries(newlyAddedCleanersByTask).some(
      ([taskId, ids]) => ids.includes(cleanerId) && !(allPreviousCleanersByTask[taskId]?.has(cleanerId))
    );
    if (isNewlyAdded) return;

    const cleaner = await resolveCleanerById(cleanerId, customerId);
    if (!cleaner?.Emailaddress) return;

    const cleanerDetails = updatedDetailsPayload.filter((d) => d.CleanerId === cleanerId);

    rescheduleEntries.push({
      cleaner: { userId: cleanerId, name: cleaner.Name, email: cleaner.Emailaddress, phone: cleaner.PhoneNumber },
      data: {
        orderInfo: buildOrderInfo(cleanerDetails),
        culture,
        organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
        userId,
        raisedFrom: userId,
        isActionRequired: true,
        sourceId: orderId,
        sourceType: "order",
        sourceDate: updatedOrderPayload.StartDate,
        inAppMessageCleaner: t("in-AppNotifications.orderRescheduledMessage", { clientName: relationData?.Name }),
        inAppManagerSuffix: t("in-AppNotifications.orderRescheduledManagerSuffix", { clientName: relationData?.Name }),
      },
    });
  }));

  await dispatchBatchNotification({ customerId, eventKey: "TASK_UPDATED", entries: rescheduleEntries });
};

const dispatchRemovalNotifications = async ({
  taskWithSubtasks,
  orderDetails,
  taskAssignments,
  allEmployees,
  updatedDetailsPayload,
  buildOrderInfo,
  culture,
  userId,
  orderId,
  updatedOrderPayload,
  relationData,
  t,
  customerId,
  dispatchBatchNotification,
}) => {
  const removalEntries = [];

  await Promise.all(taskWithSubtasks.map(async (task) => {
    const taskId = task.ProposalTaskId;
    const previousCleanerIds = [...new Set((orderDetails || []).filter((d) => d.ProposalTaskId === taskId).map((d) => d.CleanerId))];
    const currentCleanerIds = taskAssignments[taskId] || [];
    const removedCleanerIds = previousCleanerIds.filter((id) => !currentCleanerIds.includes(id));
    if (!removedCleanerIds.length) return;

    await Promise.all(removedCleanerIds.map(async (removedId) => {
      const removedCleaner = allEmployees.find((c) => c.UserId === removedId);
      if (!removedCleaner?.Emailaddress) return;

      const removedCleanerDetails = updatedDetailsPayload.filter((d) => d.ProposalTaskId === taskId);
      const detailsToUse = removedCleanerDetails.length > 0
        ? removedCleanerDetails
        : (orderDetails || []).filter((d) => d.ProposalTaskId === taskId && d.CleanerId === removedId);

      removalEntries.push({
        cleaner: { userId: removedId, name: removedCleaner.Name, email: removedCleaner.Emailaddress, phone: removedCleaner.PhoneNumber },
        data: {
          orderInfo: buildOrderInfo(detailsToUse),
          culture,
          organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
          userId,
          raisedFrom: userId,
          isActionRequired: false,
          sourceId: orderId,
          sourceType: "order",
          sourceDate: updatedOrderPayload.StartDate,
          inAppMessageCleaner: t("in-AppNotifications.orderUnassignedMessage", { clientName: relationData?.Name }),
          inAppManagerSuffix: t("in-AppNotifications.orderUnassignedManagerSuffix", { clientName: relationData?.Name }),
        },
      });
    }));
  }));

  await dispatchBatchNotification({ customerId, eventKey: "TASK_UNASSIGNED", entries: removalEntries });
};

const dispatchAssignmentNotifications = async ({
  updatedDetailsPayload,
  newlyAddedCleanersByTask,
  allEmployees,
  taskWithSubtasks,
  buildOrderInfo,
  culture,
  userId,
  orderId,
  updatedOrderPayload,
  relationData,
  location,
  t,
  customerId,
  dispatchBatchNotification,
}) => {
  const tasksByCleaner = updatedDetailsPayload.reduce((acc, d) => {
    if (!acc[d.CleanerId]) acc[d.CleanerId] = [];
    acc[d.CleanerId].push(d);
    return acc;
  }, {});

  const assignmentEntries = [];

  Object.entries(tasksByCleaner).forEach(([cleanerId, details]) => {
    const isNew = details.some((d) => newlyAddedCleanersByTask[d.ProposalTaskId]?.includes(cleanerId));
    if (!isNew) return;

    const cleaner = allEmployees.find((c) => c.UserId === cleanerId);
    if (!cleaner) return;

    const firstDetail = details.find((d) => d.StartTime);
    const task = taskWithSubtasks.find((t) => t.ProposalTaskId === firstDetail?.ProposalTaskId);

    assignmentEntries.push({
      cleaner: { userId: cleanerId, name: cleaner.Name, email: cleaner.Emailaddress, phone: cleaner.PhoneNumber },
      data: {
        orderInfo: buildOrderInfo(details),
        culture,
        organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
        userId,
        raisedFrom: userId,
        isActionRequired: true,
        sourceId: orderId,
        sourceType: "order",
        sourceDate: updatedOrderPayload.StartDate,
        inAppMessageCleaner: t("in-AppNotifications.taskAssignedMessage", { taskName: task?.taskName, clientName: relationData?.Name }),
        inAppManagerSuffix: t("in-AppNotifications.taskAssignedManagerSuffix", { taskName: task?.taskName, clientName: relationData?.Name }),
        whatsappTemplateName: `task_assignment_marketing_${culture}`,
        whatsappParameters: [cleaner.Name, task?.taskName, location, updatedOrderPayload.StartDate, updatedOrderPayload.EndDate],
      },
    });
  });

  await dispatchBatchNotification({ customerId, eventKey: "TASK_ASSIGNED", entries: assignmentEntries });
};

function EditOrder() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams();
  const orderId = (orderIdParam || "").toUpperCase();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedProposalData, setSelectedProposalData] = useState(null);
  const [taskWithSubtasks, setTaskWithSubtasks] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState({});
  const [allEmployees, setAllEmployees] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [selectedCleaners, setSelectedCleaners] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [recommendedEmployeeGroups, setRecommendedEmployeeGroups] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const toastShownRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [relationData, setRelationData] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const startDateRef = useRef(null);
  const orderDetails = orderInfo?.OrderDetails || [];
  const [taskSelectedDays, setTaskSelectedDays] = useState({});
  const [taskOneTimeDates, setTaskOneTimeDates] = useState({});
  const [showStartedModal, setShowStartedModal] = useState(false);
  const [originalDates, setOriginalDates] = useState(null);
  const [conflictMap, setConflictMap] = useState({});
  const [showPlanningConflictModal, setShowPlanningConflictModal] = useState(false);
  const [pendingConflictData, setPendingConflictData] = useState(null);
  const recommendationRequestRef = useRef(0);
  const recommendationDebounceRef = useRef(null);
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);

  const normalizeTime = (date) =>
    moment(date)
      .set({ year: 2000, month: 0, date: 1 });

  const schema = yup.object({
    editStartDate: yup
      .date()
      .required(t("startDateRequired"))
      .typeError(t("invalidStartDate")),
    editEndDate: yup
      .date()
      .nullable()
      .typeError(t("invalidEndDate"))
      .test(
        "endDate-after-startDate",
        t("endDateAfterStartDate"),
        function (endDate) {
          const { editStartDate } = this.parent;

          if (endDate && editStartDate) {
            return endDate >= editStartDate;
          }
          return true;
        }
      )
      .test(
        "endDate-not-before-taskDate",
        t("endDateBeforeTaskDate"),
        function (endDate) {
          if (!endDate) return true;

          const end = moment(endDate).startOf("day");
          const oneTimeTaskDates = taskWithSubtasks
            .filter(t => t?.recurrence?.frequency === "OneTime")
            .map(t => {
              const override = taskOneTimeDates[t.ProposalTaskId];
              return moment(override || t.recurrence?.advancedRecurrence?.date);
            })
            .filter(d => d.isValid());

          return !oneTimeTaskDates.some(d => d.startOf("day").isAfter(end));
        }
      ),

    editStartTime: yup
      .date()
      .typeError(t("invalidStartTime"))
      .required(t("startTimeRequired")),
    editEndTime: yup
      .date()
      .nullable()
      .typeError(t("invalidEndTime"))
      .test(
        "endTime-after-startTime",
        t("endTimeAfterStartTime"),
        function (endTime) {
          const { editStartTime } = this.parent;

          if (endTime && editStartTime) {
            const start = normalizeTime(editStartTime);
            const end = normalizeTime(endTime);
            return end.isSameOrAfter(start);
          }
          return true;
        }
      )
  }).concat(
    yup.object().shape(
      Object.fromEntries(
        taskWithSubtasks.map((task) => [
          [`editStartTime-${task.ProposalTaskId}`, yup
            .string()
            .required(t("startTimeRequired"))],
        ]).flat()
      )
    )
  );

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      editStartDate: selectedProposalData?.StartDate ? new Date(selectedProposalData.StartDate) : null,
      editEndDate: selectedProposalData?.EndDate ? new Date(selectedProposalData.EndDate) : null,
      editStartTime: selectedProposalData?.StartTime ? new Date(selectedProposalData.StartTime) : null,
      editEndTime: selectedProposalData?.EndTime ? new Date(selectedProposalData.EndTime) : null
    }
  });

  const {
    control,
    formState: { errors },
    reset,
    getValues,
    trigger,
    setValue,
    watch
  } = methods;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  const editStartTimeRef = watch("editStartTime");
  const watchedStartDate = watch("editStartDate");
  const watchedEndDate = watch("editEndDate");

  const [currentStep, setCurrentStep] = useState(1);
  const [activeAccordionKey, setActiveAccordionKey] = useState("1");
  const completedSteps = [1, 2, 3];

  const orderSteps = [
    {
      id: 1,
      title: t("clientAndProposal"),
      subtitle: t("clientAndProposalSelection"),
    },
    {
      id: 2,
      title: t("orderAndContractInformation"),
      subtitle: t("clientDetailsBranchContractDates"),
    },
    {
      id: 3,
      title: t("taskAssignment"),
      subtitle: t("taskScheduleEmployeeAssignments"),
    },
  ];

  const stepFields = {
    1: [],
    2: ["editStartDate", "editEndDate", "editStartTime", "editEndTime"],
    3: [],
  };

  const goToOrderStep = async (stepNumber) => {
    if (stepNumber > currentStep) {
      const fieldsToValidate = Object.entries(stepFields)
        .filter(([step]) => Number(step) < stepNumber)
        .flatMap(([, fields]) => fields);

      if (fieldsToValidate.length > 0) {
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
          toast.error(t("pleaseFillRequiredFields"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
      }
    }

    setCurrentStep(stepNumber);
    setActiveAccordionKey(String(stepNumber));
  };

  const handleOrderNext = async (e) => {
    if (e) e.preventDefault();
    const currentFields = stepFields[currentStep] || [];
    if (currentFields.length > 0) {
      const isValid = await trigger(currentFields);
      if (!isValid) {
        toast.error(t("pleaseFillRequiredFields"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
    }
    const nextStep = Math.min(currentStep + 1, 3);
    setCurrentStep(nextStep);
    setActiveAccordionKey(String(nextStep));
  };

  const handleOrderBack = (e) => {
    if (e) e.preventDefault();
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
    setActiveAccordionKey(String(prevStep));
  };

  const handleOrderSave = async (e) => {
    if (e) e.preventDefault();
    const valid = await trigger();
    if (!valid) {
      const step2HasError = ["editStartDate", "editEndDate", "editStartTime", "editEndTime"].some(
        (f) => errors[f]
      );
      if (step2HasError) {
        goToOrderStep(2);
      }
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const anyAssignments = Object.values(taskAssignments).some(
      (assigned) => Array.isArray(assigned) && assigned.length > 0
    );

    if (!anyAssignments) {
      toast.error(t("pleaseAssignTasks"), {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const endDateValue = getValues("editEndDate");
    if (isPastOrder(endDateValue)) {
      toast.error(t("cannotUpdatePastOrder"), {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const isOneTimeOrder =
      taskWithSubtasks.length > 0 &&
      taskWithSubtasks.every(
        (t) => t?.recurrence?.frequency === "OneTime"
      );

    if (isOneTimeOrder && hasOrderStartedToday()) {
      setShowStartedModal(true);
      return;
    }
    handleSaveOrder();
  };

  useEffect(() => {
    if (!editStartTimeRef || !orderDetails?.length) return;

    orderDetails.forEach((detail) => {
      setValue(`editStartTime-${detail.ProposalTaskId}`, editStartTimeRef, { shouldValidate: true });
    });

  }, [editStartTimeRef, orderDetails]);

  useEffect(() => {
    if (!taskWithSubtasks.length) return;

    taskWithSubtasks.forEach((task) => {
      const matchingDetail = orderDetails.find(
        (detail) => detail.ProposalTaskId === task.ProposalTaskId
      );

      if (matchingDetail?.StartTime) {
        const taskTime = moment.utc(matchingDetail.StartTime, moment.ISO_8601);
        if (taskTime.isValid()) {
          setValue(`editStartTime-${task.ProposalTaskId}`, taskTime.toDate(), {
            shouldDirty: true,
          });
        }
      } else {
        // Optional: set default fallback if no StartTime
        setValue(`editStartTime-${task.ProposalTaskId}`, null);
      }
    });
  }, [taskWithSubtasks, orderDetails, setValue]);


  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/editData`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        const { order, relation, tasks } = res.data;

        setOrderInfo(order);
        setRelationData(relation);
        setSelectedClient(order.RelationId);

        const customerId = localStorage.getItem("customerId");
        fetchLeaveConflictMap(customerId, order.StartDate, order.EndDate).then(setConflictMap);

        setSelectedProposalData({
          ProposalId: order.ProposalId,
          ProposalVersionId: order.ProposalVersionId,
          ProposalNumber: order.ProposalNumber,
          TemplateName: order.TemplateName,
          WorkType: order.WorkType,
          Contactperson: order.Contactperson,
        });

        const tasksWithRecurrence = (tasks || []).map((task) => ({
          ...task,
          recurrence: {
            frequency: task.Frequency,
            repeatEvery: task.RepeatEvery,
            selectedDays: safeParseSelectedDays(task.SelectedDays),
            monthlyDay: task.MonthlyDay,
            advancedRecurrence: safeParseAdvancedRecurrence(task.SelectedDays)
          },
        }));
        setTaskWithSubtasks(tasksWithRecurrence);

        const assignmentsMap = {};
        for (const detail of order.OrderDetails || []) {
          if (!assignmentsMap[detail.ProposalTaskId]) {
            assignmentsMap[detail.ProposalTaskId] = [];
          }
          if (!assignmentsMap[detail.ProposalTaskId].includes(detail.CleanerId)) {
            assignmentsMap[detail.ProposalTaskId].push(detail.CleanerId);
          }
        }
        setTaskAssignments(assignmentsMap);
      } catch (error) {
        console.error("Error fetching order details:", error);
        if (!toastShownRef.current) {
          toastShownRef.current = true;
          toast.error(t("orderNotExist"), { position: "top-right", autoClose: 5000 });
          navigate("/orders");
        }
      }
    };

    fetchOrderData();
  }, [orderId]);

  const clientOptions = relationData
    ? [{ value: selectedClient, label: relationData.Name }]
    : [];

  const proposalOptions = selectedProposalData
    ? [{
      value: selectedProposalData.ProposalVersionId,
      label: `${selectedProposalData.TemplateName || "--"} - ${selectedProposalData.ProposalNumber}`,
    }]
    : [];

  useEffect(() => {
    if (orderInfo) {

      reset({
        editStartDate: orderInfo.StartDate ? new Date(orderInfo.StartDate) : null,
        editEndDate: orderInfo.EndDate ? new Date(orderInfo.EndDate) : null,
        editStartTime: orderInfo.StartTime ? new Date(orderInfo.StartTime) : null,
        editEndTime: orderInfo.EndTime ? new Date(orderInfo.EndTime) : null,
      });
      // Store originals to detect changes later
      setOriginalDates({
        startDate: orderInfo.StartDate || null,
        endDate: orderInfo.EndDate || null,
        startTime: orderInfo.StartTime || null,
        endTime: orderInfo.EndTime || null,
      });
    }
  }, [orderInfo]);


  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "orders",
      claimValue: "edit",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);


  const getPlanningWeekText = () => {
    if (!orderInfo?.StartDate || !orderInfo?.EndDate) return "";

    const today = moment().startOf("isoWeek");
    const start = moment(orderInfo.StartDate);
    const end = moment(orderInfo.EndDate);
    const planningStart = today.isAfter(start) ? today : start;

    const startWeek = planningStart.isoWeek();
    const endWeek = end.isoWeek();
    const startYear = planningStart.isoWeekYear();
    const endYear = end.isoWeekYear();

    if (startWeek === endWeek && startYear === endYear) {
      return `Week ${startWeek}`;
    }

    return startYear === endYear
      ? `Week ${startWeek} – ${endWeek}`
      : `Week ${startWeek} (${startYear}) – Week ${endWeek} (${endYear})`;
  };

  useEffect(() => {
    const fetchAllEmployees = async () => {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/employeesForOrderAssignmentAllLocations`,
          {
            params: { customerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        setAllEmployees(res.data || []);
      } catch (err) {
        console.error("Error fetching all employees", err);
        setAllEmployees([]);
      }
    };

    fetchAllEmployees();
  }, []);

  useEffect(() => {
    const fetchLocationEmployees = async () => {
      const locationId = localStorage.getItem("LocationId");
      if (!locationId) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/employeesForOrderAssignment`,
          {
            params: { locationId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        const sortedEmployees = [...(res.data || [])].sort((a, b) =>
          (a.Name || "").localeCompare(b.Name || "")
        );
        setEmployeeList(sortedEmployees);
      } catch (err) {
        console.error("Error fetching location employees", err);
        setEmployeeList([]);
      }
    };

    fetchLocationEmployees();
  }, []);

  const getTaskOccurrenceDates = (task, orderStartDate, orderEndDate) => {
    if (!orderStartDate) return [];
    const freq = task.recurrence?.frequency || task.Frequency;
    if (freq === "OneTime") {
      const override = taskOneTimeDates[task.ProposalTaskId];
      return [override ? moment(override) : moment(orderStartDate)];
    }

    const rangeEnd = orderEndDate ? moment(orderEndDate) : moment(orderStartDate).add(6, "months");

    switch (freq) {
      case "Daily":
      case "Weekly":
        return getDailyWeeklyOccurrenceDates(task, taskSelectedDays, orderStartDate, rangeEnd);
      case "Monthly":
        return getMonthlyOccurrenceDates(task, orderStartDate, rangeEnd);
      case "Yearly":
        return getYearlyOccurrenceDates(task, orderStartDate, rangeEnd);
      default:
        return [];
    }
  };

  const getTaskOccurrenceDatesForAssign = (task, orderStartDate, orderEndDate) => {
    return getTaskOccurrenceDates(task, orderStartDate, orderEndDate);
  };

  const handleOpenAssignModal = (task) => {
    clearTimeout(recommendationDebounceRef.current);
    recommendationRequestRef.current += 1;
    setActiveTaskId(task.ProposalTaskId);
    setSelectedTask(task);
    setSelectedCleaners(taskAssignments[task.ProposalTaskId] || []);
    setRecommendedEmployeeGroups(null);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    clearTimeout(recommendationDebounceRef.current);
    recommendationRequestRef.current += 1;
    setRecommendedEmployeeGroups(null);
    setShowAssignModal(false);
  };

  useEffect(() => {
    const assignedCleaners = activeTaskId ? taskAssignments[activeTaskId] || [] : [];
    if (showAssignModal && assignedCleaners.length > 0) {
      scheduleAssignRecommendations(assignedCleaners);
    }
  }, [showAssignModal, activeTaskId]);

  const handleAssignCleaner = async (cleanersList, closeModal = false) => {
    if (!activeTaskId || !cleanersList) return;
    const requestId = ++recommendationRequestRef.current;

    const startDateRaw = getValues("editStartDate");
    const endDateRaw = getValues("editEndDate") || startDateRaw;
    const previouslyAssigned = taskAssignments[activeTaskId] || [];
    const newlyAdded = cleanersList.find(id => !previouslyAssigned.includes(id));

    if (!newlyAdded && cleanersList.length === 0) {
      setSelectedCleaners(cleanersList);
      setTaskAssignments((prev) => ({
        ...prev,
        [activeTaskId]: cleanersList,
      }));
      setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(employeeList) });
      if (closeModal) handleCloseAssignModal();
      return;
    }

    const drivingEmployeeId = newlyAdded || cleanersList[0];
    const employeeName = employeeList.find(e => e.UserId === drivingEmployeeId)?.Name || drivingEmployeeId;
    const activeTask = taskWithSubtasks.find(t => t.ProposalTaskId === activeTaskId);
    const activeStartRaw = getValues(`editStartTime-${activeTaskId}`);
    let timeWindow = null;
    let occurrenceDates = [];

    if (activeTask && activeStartRaw && startDateRaw) {
      const activeStartUtc = moment.utc(activeStartRaw);
      const startTimeOfDay = moment.utc(`1970-01-01T${activeStartUtc.format("HH:mm:ss")}`).local();
      const endTimeOfDay = startTimeOfDay.clone().add(activeTask.DurationMinutes || 60, "minutes");

      timeWindow = { startTimeOfDay, endTimeOfDay };
      occurrenceDates = getTaskOccurrenceDatesForAssign(
        activeTask,
        startDateRaw,
        endDateRaw || startDateRaw
      );
    }

    const inFormSlots = buildInFormConflictSlots({
      taskWithSubtasks,
      activeTaskId,
      taskAssignments,
      getValues,
      relationName: relationData?.Name,
    });

    const availabilityCheck = (employeeId) => checkEmployeeAvailability({
      employeeId,
      conflictMap,
      startDate: startDateRaw,
      endDate: endDateRaw,
      occurrenceDates: occurrenceDates.length > 0 ? occurrenceDates : undefined,
      timeWindow: timeWindow || undefined,
      inFormSlots,
      customerId: localStorage.getItem("customerId"),
      excludeOrderDetailId: activeTaskId,
    });
    const conflictResult = newlyAdded ? await availabilityCheck(newlyAdded) : null;

    if (
      conflictResult?.type === "leave" ||
      conflictResult?.type === "taskConflict" ||
      conflictResult?.type === "combined"
    ) {
    setPendingConflictData({
        taskId: activeTaskId,
        cleaners: cleanersList,
        employeeName,
        conflictType: conflictResult.type,
      });
      setShowPlanningConflictModal(true);
      return;
    }

    setSelectedCleaners(cleanersList);
    setTaskAssignments((prev) => ({
      ...prev,
      [activeTaskId]: cleanersList,
    }));
    if (cleanersList.length > 0) {
      const customerId = localStorage.getItem("customerId");
      fetchRecommendedEmployees(customerId, cleanersList).then(async (recommendations) => {
        if (!recommendations) {
          if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(employeeList) });
          return;
        }
        const groups = await partitionRecommendedEmployees(employeeList, recommendations, (employee) => availabilityCheck(employee.UserId));
        if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ ...groups, drivingEmployeeId });
      }).catch(() => {
        if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(employeeList) });
      });
    }
    if (closeModal) handleCloseAssignModal();
  };

  const scheduleAssignRecommendations = (cleanersList) => {
    clearTimeout(recommendationDebounceRef.current);
    recommendationDebounceRef.current = setTimeout(() => {
      void handleAssignCleaner(cleanersList, false);
    }, 275);
  };

  useEffect(() => {
    if (!orderDetails?.length || !taskWithSubtasks.length) return;

    const selectedByTask = {};

    taskWithSubtasks.forEach(task => {
      const daysSet = new Set();

      orderDetails
        .filter(d => d.ProposalTaskId === task.ProposalTaskId)
        .forEach(d => {
          if (d.DayOfWeek) {
            daysSet.add(d.DayOfWeek.toLowerCase());
          }
        });

      selectedByTask[task.ProposalTaskId] = Array.from(daysSet);
    });

    setTaskSelectedDays(selectedByTask);
  }, [orderDetails, taskWithSubtasks]);

  useEffect(() => {
    if (!taskWithSubtasks.length) return;

    const init = {};
    taskWithSubtasks.forEach(task => {
      if (task.recurrence?.frequency !== "OneTime") return;
      const matchingDetail = orderDetails.find(d => d.ProposalTaskId === task.ProposalTaskId);
      const rawDate = matchingDetail?.WeekStartDate || task.recurrence?.advancedRecurrence?.date;
      init[task.ProposalTaskId] = rawDate ? new Date(rawDate) : null;
    });
    setTaskOneTimeDates(init);
  }, [orderDetails, taskWithSubtasks]);

  const hasOrderStartedToday = () => {
    const now = moment();

    return (orderDetails || []).some(detail => {
      if (!detail.StartTime || !detail.WeekStartDate) return false;

      const taskDate = moment(detail.WeekStartDate).format("YYYY-MM-DD");
      const taskTime = moment(detail.StartTime).format("HH:mm:ss");

      const taskDateTime = moment(`${taskDate} ${taskTime}`);

      return taskDateTime.isSame(moment(), "day") && taskDateTime.isBefore(now);
    });
  };

  const resolveCleanerById = async (cleanerId, customerId) => {
    let cleaner = allEmployees.find(c => c.UserId === cleanerId);
    if (!cleaner) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/employeesForOrderAssignmentAllLocations`,
          {
            params: { customerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          }
        );
        cleaner = res.data?.find(u => u.UserId === cleanerId) || null;
      } catch (err) {
        // silent
      }
    }
    return cleaner || null;
  };

  const buildLocationString = () =>
    [relationData?.StreetName, relationData?.HouseNumber, relationData?.PostCode, relationData?.City]
      .filter(Boolean)
      .join(", ");

  const dispatchBatchNotification = async ({ customerId, eventKey, entries, isRemoval = false }) => {
    if (!entries.length) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/notificationSettingsRoute/dispatch-batch`,
        {
          CustomerId: customerId, EventKey: eventKey, IsRemoval: isRemoval,
          Entries: entries.map(e => ({ Cleaner: e.cleaner, Data: e.data }))
        },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
    } catch (err) {
      console.error(`Batch dispatch failed for event ${eventKey}`, err);
    }
  };

  const runOrderUpdateNotifications = async ({
    taskAssignments,
    newlyAddedCleanersByTask,
    allPreviousCleanersByTask,
    updatedDetailsPayload,
    updatedOrderPayload,
    datesOrTimesChanged,
    userId,
    customerId,
  }) => {
    const location = buildLocationString();
    const culture = localStorage.getItem("culture") || "nl";

    const buildOrderInfo = (cleanerDetails) => {
      const uniqueTaskMap = new Map();
      cleanerDetails.forEach((detail) => {
        if (!uniqueTaskMap.has(detail.TaskId)) {
          const taskData = taskWithSubtasks.find((t) => t.ProposalTaskId === detail.ProposalTaskId);
          const freq = taskData?.recurrence?.frequency?.toLowerCase();
          uniqueTaskMap.set(detail.TaskId, {
            taskName: taskData?.taskName,
            startTime: detail.StartTime ? moment(detail.StartTime).format("HH:mm") : null,
            endTime: detail.EndTime ? moment(detail.EndTime).format("HH:mm") : null,
            frequency: taskData?.recurrence?.frequency === "OneTime"
              ? t("onetime")
              : `${getOccurrenceCount(taskData?.recurrence)}x ${t(freq)}`,
          });
        }
      });
      return {
        clientName: relationData?.Name || selectedProposalData?.Contactperson,
        location,
        startDate: updatedOrderPayload.StartDate,
        endDate: updatedOrderPayload.EndDate,
        tasks: Array.from(uniqueTaskMap.values()),
      };
    };

    if (datesOrTimesChanged) {
      await dispatchRescheduleNotifications({
        taskAssignments,
        newlyAddedCleanersByTask,
        allPreviousCleanersByTask,
        resolveCleanerById,
        customerId,
        updatedDetailsPayload,
        buildOrderInfo,
        culture,
        userId,
        orderId,
        updatedOrderPayload,
        relationData,
        t,
        dispatchBatchNotification,
      });
    }

    await dispatchRemovalNotifications({
      taskWithSubtasks,
      orderDetails,
      taskAssignments,
      allEmployees,
      updatedDetailsPayload,
      buildOrderInfo,
      culture,
      userId,
      orderId,
      updatedOrderPayload,
      relationData,
      t,
      customerId,
      dispatchBatchNotification,
    });

    await dispatchAssignmentNotifications({
      updatedDetailsPayload,
      newlyAddedCleanersByTask,
      allEmployees,
      taskWithSubtasks,
      buildOrderInfo,
      culture,
      userId,
      orderId,
      updatedOrderPayload,
      relationData,
      location,
      t,
      customerId,
      dispatchBatchNotification,
    });
  };

  const handleSaveOrder = async () => {
    const valid = await trigger();
    if (!valid) return;

    setIsSaving(true);

    try {
      const userId = localStorage.getItem("UserId");
      const customerId = localStorage.getItem("customerId");

      const hasWeekdays = validateTaskFrequencies(taskWithSubtasks, taskSelectedDays);
      if (!hasWeekdays) {
        toast.error(t("selectAtLeastOneWeekday"), {
          position: "top-right",
          autoClose: 2000,
        });
        setIsSaving(false);
        return;
      }

      const today = moment().startOf("day");
      const startDateRaw = getValues("editStartDate");
      const endDateRaw = getValues("editEndDate");
      const orderStart = moment(startDateRaw).startOf("day");

      const oneTimeValidation = validateOneTimeDates(taskWithSubtasks, taskOneTimeDates, orderStart, today);
      if (!oneTimeValidation.valid) {
        toast.error(t(oneTimeValidation.errorKey), {
          position: "top-right",
          autoClose: 2500,
        });
        setIsSaving(false);
        return;
      }

      const orderEnd = calculateEffectiveOrderEnd(orderStart, endDateRaw, taskWithSubtasks, taskOneTimeDates);
      const planningStart = moment.max(orderStart, today);

      const startTimeRaw = getValues("editStartTime");
      const endTimeRaw = getValues("editEndTime");
      const startTime = startTimeRaw ? moment(startTimeRaw) : null;
      const endTime = endTimeRaw ? moment(endTimeRaw) : null;

      const updatedOrderPayload = {
        OrderId: orderId,
        StartDate: orderStart.format("YYYY-MM-DD"),
        EndDate: orderEnd.format("YYYY-MM-DD"),
        StartTime: startTime ? startTime.toISOString() : null,
        EndTime: endTime ? endTime.toISOString() : null,
        UpdatedAt: moment().toISOString(),
        UpdatedBy: userId,
      };

      const allPreviousCleanersByTask = {};
      (orderDetails || []).forEach((detail) => {
        if (!allPreviousCleanersByTask[detail.ProposalTaskId]) {
          allPreviousCleanersByTask[detail.ProposalTaskId] = new Set();
        }
        allPreviousCleanersByTask[detail.ProposalTaskId].add(detail.CleanerId);
      });

      const newlyAddedCleanersByTask = {};
      Object.entries(taskAssignments).forEach(([taskId, cleaners]) => {
        const previousCleaners = allPreviousCleanersByTask[taskId] || new Set();
        const newCleaners = cleaners.filter((cleanerId) => !previousCleaners.has(cleanerId));
        if (newCleaners.length > 0) {
          newlyAddedCleanersByTask[taskId] = newCleaners;
        }
      });

      const updatedDetailsPayload = [];
      taskWithSubtasks.forEach((task) => {
        const assignedCleaners = taskAssignments[task.ProposalTaskId] || [];
        if (!assignedCleaners.length) return;

        const occurrenceDates = getTaskOccurrenceDates(task, planningStart, orderEnd);
        occurrenceDates.forEach((taskDate) => {
          assignedCleaners.forEach((cleanerId) => {
            updatedDetailsPayload.push(
              createSingleOrderDetail({
                task,
                cleanerId,
                taskDate,
                orderId,
                getValues,
              })
            );
          });
        });
      });

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/orderRoute/updateOrder/${orderId}`,
        {
          order: updatedOrderPayload,
          orderDetails: updatedDetailsPayload,
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      if (response.status === 200) {
        clearEmployeeAvailabilityCache();
        toast.success(t("orderUpdated"), { position: "top-right", autoClose: 3000 });
        navigate("/orders");

        const datesOrTimesChanged = checkDatesOrTimesChanged(
          originalDates,
          orderStart,
          orderEnd,
          startTimeRaw,
          endTimeRaw
        );

        runOrderUpdateNotifications({
          taskAssignments,
          newlyAddedCleanersByTask,
          allPreviousCleanersByTask,
          updatedDetailsPayload,
          updatedOrderPayload,
          datesOrTimesChanged,
          userId,
          customerId,
        }).catch((err) => console.error("Background notification error:", err));
      }
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(t("orderSaveFailed"), {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderStatusBadge = (status) => {
    const statusKey = status?.toLowerCase?.();
    const translatedText = t(statusKey) || status;

    const icon = {
      draft: faStream,
      active: faCheck,
      paused: faBan,
      completed: faCheck,
    }[statusKey];

    const variant =
      {
        draft: "primary",
        active: "info",
        paused: "danger",
        completed: "success",
      }[statusKey] || "secondary";

    return (
      <SubtleBadge
        pill
        bg={variant}
        className="d-inline-flex align-items-center px-2 py-1"
      >
        <p className="mb-0 data-font-size">{translatedText}</p>
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            transform="shrink-2"
            className="ms-1 data-font-size"
          />
        )}
      </SubtleBadge>
    );
  };

  const isPastOrder = (endDateValue) => {
    if (!endDateValue) return false;
    const today = moment().startOf("day");
    const formEndDate = moment(endDateValue).startOf("day");
    return formEndDate.isBefore(today);
  };

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect;
  }

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background
              image={corner4}
              className="bg-card"
            />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("editOrder")}</h5>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/orders"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("orders")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      as={Link}
                      onClick={() =>
                        navigate(
                          paths.orderDetails.replace(":orderId", orderId.toLowerCase())
                        )
                      }
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {orderInfo?.OrderNumber || t("orderDetails")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("edit")}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {!orderInfo ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-stretch">
            {/* ── LEFT SIDEBAR ── */}
            <Col
              xs="12"
              lg="3"
              xl="2"
              className="rf-left-panel step-sidebar-col"
            >
              <Card className="mb-3 h-100 step-row-container order-step-container">
                <Card.Body>
                  {orderSteps.map((step, index) => (
                    <div
                      key={step.id}
                      role="button"
                      tabIndex={0}
                      className={`d-flex gap-2 pointer p-2 rounded step-row ${
                        currentStep === step.id ? "step-row-active" : ""
                      }`}
                      onClick={() => goToOrderStep(step.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToOrderStep(step.id);
                        }
                      }}
                    >
                      <div className="d-flex flex-column align-items-center step-circle-col">
                        <span
                          className={`step-circle ${
                            currentStep === step.id ? "active" : ""
                          } ${
                            completedSteps.includes(step.id)
                              ? "completed"
                              : ""
                          }`}
                        >
                          {step.id}
                        </span>
                        {index < orderSteps.length - 1 && (
                          <div
                            className={`step-connector-line ${
                              completedSteps.includes(step.id)
                                ? "completed"
                                : ""
                            } ${
                              currentStep === step.id ? "active" : ""
                            }`}
                          />
                        )}
                      </div>
                      <div className="step-text-col">
                        <h5
                          className={`fw-medium ${
                            currentStep === step.id
                              ? "text-primary"
                              : ""
                          }`}
                        >
                          {step.title}
                        </h5>
                        {step.subtitle ? (
                          <small className="mt-2 text-secondary">
                            {step.subtitle}
                          </small>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            {/* ── RIGHT PANEL ── */}
            <Col
              xs="12"
              lg="9"
              xl="10"
              className="rf-right-panel rf-form-col"
            >
              <Accordion
                activeKey={activeAccordionKey}
                onSelect={(key) => {
                  if (key) goToOrderStep(Number(key));
                }}
                className="rf-accordion"
              >
                {/* ── STEP 1: Client & Proposal ── */}
                <Accordion.Item eventKey="1" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-client">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <span className="rf-header-text">
                        <span>{t("clientAndProposal")}</span>
                        <span className="rf-header-subtitle">
                          {t("clientAndProposalSelection")}
                        </span>
                      </span>
                    </span>
                  </Accordion.Header>
                <Accordion.Body id="order-proposal-select-card">
                    <Row className="g-4 mb-3">
                      {/* Client Select */}
                      <Col xs={12} md={5}>
                        <Form.Group className="mb-2 mb-md-0">
                          <Form.Label htmlFor="clientSelect" className="data-font-size data-font-colour mb-2">
                            {t("client")}
                          </Form.Label>
                          <Select
                            id="clientSelect"
                            isDisabled={true}
                            options={clientOptions}
                            value={clientOptions.find((opt) => opt.value === selectedClient)}
                            onChange={(selectedOption) => setSelectedClient(selectedOption?.value || "")}
                            placeholder={t("searchLabel")}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                            isClearable
                            className="custom-select-container"
                            classNamePrefix="custom-select"
                          />
                        </Form.Group>
                      </Col>

                      {/* Proposal Select */}
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label htmlFor="proposalSelect" className="data-font-size data-font-colour mb-2">
                            {t("proposals")}
                          </Form.Label>
                          <Select
                            id="proposalSelect"
                            options={proposalOptions}
                            value={proposalOptions[0] || null}
                            onChange={() => { }}
                            isDisabled={true}
                            placeholder={t("searchLabel")}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                            isClearable
                            className="custom-select-container"
                            classNamePrefix="custom-select"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

               {/* ── STEP 2: Order & Contract Information ── */}
                <Accordion.Item eventKey="2" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center justify-content-between w-100 pe-3 data-font-colour">
                      <div className="d-flex align-items-center">
                        <span className="rf-header-icon bg-address">
                          <FontAwesomeIcon icon={faCalendarAlt} />
                        </span>
                        <span className="rf-header-text">
                          <span>{t("orderAndContractInformation")}</span>
                          <span className="rf-header-subtitle">
                            {t("clientDetailsBranchContractDates")}
                          </span>
                        </span>
                      </div>
                      {orderInfo && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semi-bold data-font-colour data-font-size fs-9">
                            {t("orderStatus")}:
                          </span>
                          <span className="fw-semi-bold data-font-colour data-font-size">
                            {renderStatusBadge(orderInfo.Status)}
                          </span>
                        </div>
                      )}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {selectedProposalData ? (
                      <div className="pt-2">
                        <Row className="g-4">
                          {/* ── Column 1: Client Details ── */}
                          <Col xs={12} md={5}>
                            <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size">
                              {t("clientInformation")}
                            </h6>

                            {/* Contact Person */}
                            <div className="mb-3">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("contactPerson")}
                              </Form.Label>
                              <div className="text-700 data-font-size">
                                {selectedProposalData.Contactperson || "-"}
                              </div>
                            </div>

                            {/* Branch */}
                            <div>
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("branch")}
                              </Form.Label>
                              <div className="text-700 data-font-size">
                                {[relationData?.StreetName, relationData?.PostCode, relationData?.City].filter(Boolean).join(", ") || "-"}
                              </div>
                            </div>
                          </Col>

                          {/* ── Column 2: Contract Duration ── */}
                          <Col xs={12} md={7} className="border-start-md ps-md-4">
                            <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size">
                              {t("contractDuration")}
                            </h6>

                            {/* Row 1: Start Date & Start Time */}
                            <Row className="g-3 mb-3">
                              {/* Start Date */}
                              <Col xs={12} sm={5}>
                                <Form.Group controlId="editStartDate" className="order-date-input">
                                  <Form.Label className="data-font-size data-font-colour mb-2">
                                    {t("startDate")} <span className="text-danger">*</span>
                                  </Form.Label>
                                  <div className={`date-picker-wrapper position-relative ${errors.editStartDate ? "is-invalid-wrapper" : ""}`}>
                                    <Controller
                                      control={control}
                                      name="editStartDate"
                                      render={({ field }) => (
                                        <DatePicker
                                          ref={startDateRef}
                                          selected={field.value}
                                          onChange={(date) => {
                                            field.onChange(date);
                                            setIsStartDateOpen(false);
                                            trigger("editEndDate");
                                          }}
                                          open={isStartDateOpen}
                                          onInputClick={() => setIsStartDateOpen(true)}
                                          onClickOutside={() => setIsStartDateOpen(false)}
                                          autoComplete="off"
                                          dateFormat="dd-MM-yyyy"
                                          placeholderText="dd-mm-yyyy"
                                          className={`form-control form-control-sm pe-4 ${errors.editStartDate ? "is-invalid" : ""}`}
                                          portalId="date-picker-portal"
                                        />
                                      )}
                                    />
                                    <span className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none" onClick={() => setIsEndDateOpen((prev) => !prev)}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                    </span>
                                  </div>
                                  {errors.editStartDate && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {errors.editStartDate?.message}
                                    </Form.Control.Feedback>
                                  )}
                                </Form.Group>
                              </Col>

                              {/* Start Time */}
                              <Col xs={12} sm={5} >
                                <Form.Group controlId="editStartTime" className="order-time-input">
                                  <Form.Label className="data-font-size data-font-colour mb-2">
                                    {t("startingTime")} <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Controller
                                    control={control}
                                    name="editStartTime"
                                    render={({ field }) => (
                                      <DatePicker
                                        selected={
                                          field.value
                                            ? (() => {
                                                const timeUtc = moment.utc(field.value);
                                                const dateStr = watch("editStartDate")
                                                  ? moment(watch("editStartDate")).format("YYYY-MM-DD")
                                                  : moment().format("YYYY-MM-DD");
                                                const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                                return combined.toDate();
                                              })()
                                            : undefined
                                        }
                                        openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                        onChange={(time) => {
                                          field.onChange(time);
                                          trigger("editEndTime");
                                        }}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="HH:mm"
                                        timeFormat="HH:mm"
                                        placeholderText="HH:mm"
                                        autoComplete="off"
                                        className={`form-control form-control-sm ${errors.editStartTime ? "is-invalid" : ""}`}
                                        customInput={
                                          <TimeInput className="form-control form-control-sm" autoComplete="off" />
                                        }
                                      />
                                    )}
                                  />
                                  {errors.editStartTime && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {errors.editStartTime?.message}
                                    </Form.Control.Feedback>
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>

                            {/* Row 2: End Date & End Time */}
                            <Row className="g-3">
                              {/* End Date */}
                              <Col xs={12} sm={5}>
                                <Form.Group controlId="editEndDate" className="order-date-input">
                                  <Form.Label className="data-font-size data-font-colour mb-2">
                                    {t("endDate")}
                                  </Form.Label>
                                  <div className={`date-picker-wrapper position-relative ${errors.editEndDate ? "is-invalid-wrapper" : ""}`}>
                                    <Controller
                                      control={control}
                                      name="editEndDate"
                                      render={({ field }) => (
                                        <DatePicker
                                          selected={field.value}
                                          onChange={(date) => {
                                            field.onChange(date);
                                            setIsEndDateOpen(false);
                                          }}
                                          open={isEndDateOpen}
                                          onInputClick={() => setIsEndDateOpen(true)}
                                          onClickOutside={() => setIsEndDateOpen(false)}
                                          autoComplete="off"
                                          dateFormat="dd-MM-yyyy"
                                          placeholderText="dd-mm-yyyy"
                                          className={`form-control form-control-sm pe-4 ${errors.editEndDate ? "is-invalid" : ""}`}
                                          portalId="date-picker-portal"
                                        />
                                      )}
                                    />
                                    <span className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none" onClick={() => setIsEndDateOpen((prev) => !prev)}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                    </span>
                                  </div>
                                  {errors.editEndDate && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {errors.editEndDate?.message}
                                    </Form.Control.Feedback>
                                  )}
                                </Form.Group>
                              </Col>

                              {/* End Time */}
                              <Col xs={12} sm={5} >
                                <Form.Group controlId="editEndTime" className="order-time-input">
                                  <Form.Label className="data-font-size data-font-colour mb-2">
                                    {t("endTime")}
                                  </Form.Label>
                                  <Controller
                                    control={control}
                                    name="editEndTime"
                                    render={({ field }) => (
                                      <DatePicker
                                        selected={
                                          field.value
                                            ? (() => {
                                                const timeUtc = moment.utc(field.value);
                                                const dateStr = watch("editStartDate")
                                                  ? moment(watch("editStartDate")).format("YYYY-MM-DD")
                                                  : moment().format("YYYY-MM-DD");
                                                const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                                return combined.toDate();
                                              })()
                                            : undefined
                                        }
                                        openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                        onChange={field.onChange}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="HH:mm"
                                        timeFormat="HH:mm"
                                        placeholderText="HH:mm"
                                        autoComplete="off"
                                        className={`form-control form-control-sm ${errors.editEndTime ? "is-invalid" : ""}`}
                                        customInput={
                                          <TimeInput className="form-control form-control-sm" autoComplete="off" />
                                        }
                                      />
                                    )}
                                  />
                                  {errors.editEndTime && (
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {errors.editEndTime?.message}
                                    </Form.Control.Feedback>
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        {t("selectProposalToViewDetail")}
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                {/* ── STEP 3: Task Assignment ── */}
                <Accordion.Item eventKey="3" className="rf-accordion-item">
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center justify-content-between w-100 pe-3 data-font-colour">
                      <div className="d-flex align-items-center">
                        <span className="rf-header-icon bg-invoice">
                          <FontAwesomeIcon icon={faUserPlus} />
                        </span>
                        <span className="rf-header-text">
                          <span>{t("taskAssignment")}</span>
                          <span className="rf-header-subtitle">
                            {t("taskScheduleEmployeeAssignments")}
                          </span>
                        </span>
                      </div>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="d-flex justify-content-start align-items-center mb-3">
                      <span className="text-muted fs-9">
                        {t("planningWeek")}: {getPlanningWeekText()}
                      </span>
                    </div>

                    {taskWithSubtasks.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        {t("noTasksAvailable")}
                      </div>
                    ) : (
                      taskWithSubtasks.map((task, i) => (
                        <div key={task.ProposalTaskId || i} className="border rounded p-3 mb-3 bg-white shadow-sm">
                          <Row>
                            <Col md={9}>
                              <Row className="align-items-center mb-2">
                                <Col md={8}>
                                  <p className="mb-0 fw-semibold fs-9 text-dark">
                                    {i + 1}. {task.taskName} (
                                    {task.recurrence.frequency === "OneTime"
                                      ? t(`frequencyLabel.${task.recurrence.frequency.toLowerCase()}`)
                                      : `${getOccurrenceCount(task.recurrence)}x ${t(
                                        `frequencyLabel.${task.recurrence.frequency.toLowerCase()}`
                                      )}`}
                                    )
                                  </p>
                                </Col>
                                <Col md={4} className="my-2 my-md-0">
                                  {task.recurrence?.frequency === "OneTime" ? (
                                    <div className="task-row-date-input-wrap d-inline-block position-relative date-picker-wrapper">
                                      <DatePicker
                                        selected={taskOneTimeDates[task.ProposalTaskId] || null}
                                        onChange={(date) => {
                                          if (!date) {
                                            setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: null }));
                                            return;
                                          }
                                          const selected = moment(date).startOf("day");
                                          const today = moment().startOf("day");
                                          if (selected.isBefore(today)) {
                                            toast.error(t("taskDateCannotBePast"), {
                                              position: "top-right",
                                              autoClose: 2500,
                                            });
                                            return;
                                          }
                                          if (watchedStartDate && selected.isBefore(moment(watchedStartDate).startOf("day"))) {
                                            toast.error(t("taskDateBeforeStartDate"), {
                                              position: "top-right",
                                              autoClose: 2500,
                                            });
                                            return;
                                          }
                                          if (watchedEndDate && selected.isAfter(moment(watchedEndDate).startOf("day"))) {
                                            toast.error(t("taskDateAfterEndDate"), {
                                              position: "top-right",
                                              autoClose: 2500,
                                            });
                                            return;
                                          }
                                          setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: date }));
                                        }}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="dd-mm-yyyy"
                                        className="form-control form-control-sm pe-4"
                                        portalId="date-picker-portal"
                                      />
                                      <span className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                      </span>
                                    </div>
                                  ) : task.recurrence?.selectedDays?.length > 0 ? (
                                    <div className="d-flex flex-wrap justify-content-center justify-content-md-start">
                                      {[...task.recurrence.selectedDays]
                                        .sort(
                                          (a, b) =>
                                            [
                                              "monday", "tuesday", "wednesday",
                                              "thursday", "friday", "saturday", "sunday"
                                            ].indexOf(a.toLowerCase()) -
                                            [
                                              "monday", "tuesday", "wednesday",
                                              "thursday", "friday", "saturday", "sunday"
                                            ].indexOf(b.toLowerCase())
                                        )
                                        .map((day) => {
                                          const isSelected =
                                            taskSelectedDays[task.ProposalTaskId]?.includes(day.toLowerCase());

                                          return (
                                            <span
                                              key={day}
                                              role="button"
                                              onClick={() => {
                                                setTaskSelectedDays(prev => {
                                                  const current =
                                                    prev[task.ProposalTaskId] || [];

                                                  const updated = current.includes(day.toLowerCase())
                                                    ? current.filter(d => d !== day.toLowerCase())
                                                    : [...current, day.toLowerCase()];

                                                  return {
                                                    ...prev,
                                                    [task.ProposalTaskId]: updated
                                                  };
                                                });
                                              }}
                                              className={`badge fw-medium me-1 mb-1 border cursor-pointer ${isSelected
                                                ? "bg-success-subtle text-success"
                                                : "bg-light text-muted"
                                                }`}
                                            >
                                              {t(`days.${day.toLowerCase()}`)}
                                            </span>
                                          );
                                        })}
                                    </div>
                                  ) : (
                                    <span className="text-muted">--</span>
                                  )}
                                </Col>
                              </Row>

                              <Row>
                                <Col>
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    {(taskAssignments[task.ProposalTaskId] || []).map((cleanerId) => {
                                      const cleaner = (allEmployees || []).find(
                                        (emp) => emp.UserId === cleanerId
                                      ) || (employeeList || []).find(
                                        (emp) => emp.UserId === cleanerId
                                      );
                                      if (!cleaner) return null;

                                      return (
                                        <div
                                          key={cleanerId}
                                          className="d-flex align-items-center rounded-pill px-3 py-1 bg-secondary-subtle text-default fs-10 position-relative"
                                        >
                                          <FontAwesomeIcon icon="user" className="me-2 text-default" />
                                          <span>{cleaner.Name}</span>
                                          <div
                                            className="position-absolute remove-cleaner-icon"
                                            onClick={() => {
                                              const updatedCleaners = (
                                                taskAssignments[task.ProposalTaskId] || []
                                              ).filter((id) => id !== cleanerId);

                                              setTaskAssignments((prev) => ({
                                                ...prev,
                                                [task.ProposalTaskId]: updatedCleaners,
                                              }));
                                            }}
                                          >
                                            <FontAwesomeIcon icon="times" size="xs" className="text-danger" />
                                          </div>
                                        </div>
                                      );
                                    })}
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      type="button"
                                      onClick={() => handleOpenAssignModal(task)}
                                      title={t("assign")}
                                    >
                                      <FontAwesomeIcon icon="user-plus" />
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                            </Col>

                            {/* Starts at Time Input */}
                            <Col md={3} className="d-flex justify-content-start justify-content-md-end align-items-start mb-2 mt-2 mt-md-0" id="task-start-time-col">
                              <div className="d-flex align-items-center gap-2">
                                <span className="data-font-colour data-font-size text-nowrap field-label">
                                  {t("startsAt")}
                                </span>

                                <div className="task-time-input-wrapper">
                                  <Controller
                                    control={control}
                                    name={`editStartTime-${task.ProposalTaskId}`}
                                    rules={{ required: t("startTimeRequired") }}
                                    render={({ field }) => (
                                      <DatePicker
                                        {...field}
                                        selected={
                                          field.value
                                            ? (() => {
                                              const timeUtc = moment.utc(field.value);
                                              const watchedStartDate = watch("editStartDate");

                                              if (watchedStartDate) {
                                                const dateStr = moment(watchedStartDate).format("YYYY-MM-DD");
                                                const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                                return combined.toDate();
                                              }

                                              return field.value;
                                            })()
                                            : undefined
                                        }
                                        openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        timeCaption="Time"
                                        dateFormat="HH:mm"
                                        timeFormat="HH:mm"
                                        placeholderText="HH:mm"
                                        className={`form-control form-control-sm narrow-width-sm ${errors[`editStartTime-${task.ProposalTaskId}`] ? "is-invalid" : ""}`}
                                        onChange={(val) => field.onChange(val)}
                                        customInput={
                                          <TimeInput className="form-control form-control-sm narrow-width-sm" />
                                        }
                                      />
                                    )}
                                  />
                                  <Form.Control.Feedback type="invalid" className="d-block">
                                    {errors[`editStartTime-${task.ProposalTaskId}`]?.message}
                                  </Form.Control.Feedback>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      ))
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* ── ACTION CARD (Back / Next / Save / Cancel) ── */}
              <Card className="mt-3">
                <Card.Body>
                  <Row className="justify-content-between align-items-center">
                    <Col xs="auto">
                      <Button
                        variant="falcon-default"
                        size="sm"
                        type="button"
                        onClick={() => navigate("/orders")}
                      >
                        {t("cancel")}
                      </Button>
                    </Col>
                    <Col xs="auto">
                      {currentStep > 1 && (
                        <Button
                          variant="falcon-default"
                          size="sm"
                          className="me-2"
                          type="button"
                          onClick={handleOrderBack}
                        >
                          {t("back")}
                        </Button>
                      )}
                      {currentStep < 3 ? (
                        <Button
                          variant="primary"
                          size="sm"
                          type="button"
                          onClick={handleOrderNext}
                        >
                          {t("next")}{" "}
                          <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          type="button"
                          onClick={handleOrderSave}
                          disabled={isSaving}
                        >
                          {isSaving ? t("saving") : t("save")}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}

      <Modal
        show={showAssignModal}
        onHide={handleCloseAssignModal}
        centered
        backdrop="static"
        keyboard={false}
        className={showPlanningConflictModal ? "d-none" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-muted fs-9">
            {t("assignCleaner")} {t("to")}{" "}
            {selectedTask?.taskName && <span>– {selectedTask.taskName}</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("selectCleaner")}</Form.Label>
            <MultiSelect
              noOptionsMessage={() => t("noOptionsAvailable")}
              isMulti
              closeMenuOnSelect={false}
              options={recommendedEmployeeGroups?.recommended?.length > 0 ? [
                {
                  label: t("recommendedEmployees"),
                  options: recommendedEmployeeGroups.recommended.map((cleaner) => ({
                    value: cleaner.UserId,
                    label: cleaner.Name,
                  })),
                },
                {
                  label: t("allEmployees"),
                  options: recommendedEmployeeGroups.others.map((cleaner) => ({
                    value: cleaner.UserId,
                    label: cleaner.Name,
                  })),
                },
              ] : sortEmployeesAscending(employeeList).map((cleaner) => ({
                value: cleaner.UserId,
                label: cleaner.Name,
              }))}
              value={employeeList
                .filter((emp) => selectedCleaners.includes(emp.UserId))
                .map((emp) => ({
                  value: emp.UserId,
                  label: emp.Name,
                }))}
              onChange={(selected) => {
                const newValues = selected.map((item) => item.value);
                scheduleAssignRecommendations(newValues);
              }}
            />
          </Form.Group>

          {selectedCleaners?.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              {selectedCleaners.map((id) => {
                const cleaner = employeeList.find((c) => c.UserId === id);
                if (!cleaner) return null;

                return (
                  <div
                    key={id}
                    className="d-flex align-items-center px-3 py-1 rounded-pill bg-light border text-dark small shadow-sm"
                  >
                    <FontAwesomeIcon
                      icon="user"
                      className="me-2 text-primary"
                    />
                    <span className="fw-semibold">{cleaner.Name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="falcon-default"
            onClick={handleCloseAssignModal}
          >
            {t("cancel")}
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={() => handleAssignCleaner(selectedCleaners, true)}
            disabled={!selectedCleaners || selectedCleaners.length === 0}
          >
            {t("assign")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStartedModal}
        onHide={() => setShowStartedModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("orderAlreadyStarted")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {t("orderStartedWarning")}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowStartedModal(false);
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              setShowStartedModal(false);
              // Force RHF to update all fields
              const valid = await trigger();
              if (!valid) return;
              handleSaveOrder();
            }}
          >
            {t("yes")}
          </Button>
        </Modal.Footer>
      </Modal>

      <PlanningConflictModal
        show={showPlanningConflictModal}
        employeeName={pendingConflictData?.employeeName}
        conflictType={pendingConflictData?.conflictType}
        t={t}
        onCancel={() => {
          setShowPlanningConflictModal(false);
          setPendingConflictData(null);
        }}
        onConfirm={() => {
          if (pendingConflictData) {
            setSelectedCleaners(pendingConflictData.cleaners);
            setTaskAssignments((prev) => ({
              ...prev,
              [pendingConflictData.taskId]: pendingConflictData.cleaners,
            }));
          }
          setShowPlanningConflictModal(false);
          setPendingConflictData(null);
        }}
      />
    </>
  );
}

export default EditOrder;